import { useCallback, useEffect, useState } from 'react'

import { useLocalStorage } from '../hooks/useLocalStorage'

const THEME_OPTIONS = ['light', 'dark', 'system'] as const
type Theme = (typeof THEME_OPTIONS)[number]

const getIsDarkmode = (theme: Theme, isDarkColorSchemePrefered: boolean) =>
  theme === 'dark' || (theme === 'system' && isDarkColorSchemePrefered)

const getStyleFromClass = (className: string, property: string) => {
  const el = document.createElement('div')
  el.style.display = 'none'
  el.className = className
  document.body.appendChild(el)
  const value = getComputedStyle(el).getPropertyValue(property)
  document.body.removeChild(el)
  return value
}

const matchMediaDark = window.matchMedia('(prefers-color-scheme: dark)')

export const useThemeContext = () => {
  const [theme = 'system', setTheme] = useLocalStorage<Theme>('theme')

  const [isDarkColorSchemePrefered, setIsDarkColorSchemePrefered] = useState(
    matchMediaDark.matches,
  )

  const isDarkMode = getIsDarkmode(theme, isDarkColorSchemePrefered)

  const getTailwindStyle = useCallback(
    (
      name:
        | 'ink'
        | 'inkSecondary'
        | 'inkTertiary'
        | 'border'
        | 'borderEmphasis'
        | `--${string}-${string}`,
    ) => {
      const styles = getComputedStyle(document.documentElement)

      if (name === 'ink') {
        return getStyleFromClass(
          isDarkMode ? 'dark:text-amaze-inkBright' : 'text-amaze-ink',
          'color',
        )
      }

      if (name === 'inkSecondary') {
        return getStyleFromClass(
          isDarkMode ? 'dark:text-amaze-inkBright/75' : 'text-amaze-ink/75',
          'color',
        )
      }

      if (name === 'inkTertiary') {
        return getStyleFromClass(
          isDarkMode ? 'dark:text-amaze-inkBright/50' : 'text-amaze-ink/50',
          'color',
        )
      }

      if (name === 'border') {
        return styles.getPropertyValue(
          isDarkMode ? '--color-slate-800' : '--color-slate-100',
        )
      }

      if (name === 'borderEmphasis') {
        return styles.getPropertyValue(
          isDarkMode ? '--color-slate-700' : '--color-slate-200',
        )
      }

      return styles.getPropertyValue(name)
    },
    [isDarkMode],
  )

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode)
  }, [isDarkMode])

  useEffect(() => {
    const matchMediaListener = (event: MediaQueryListEvent) => {
      setIsDarkColorSchemePrefered(event.matches)
    }
    matchMediaDark.addEventListener('change', matchMediaListener)

    return () => {
      matchMediaDark.removeEventListener('change', matchMediaListener)
    }
  }, [])

  return {
    theme,
    setTheme,
    themeOptions: THEME_OPTIONS,
    isDarkMode,
    getTailwindStyle,
  }
}
